/* tslint:disable:no-string-literal */
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {NavController, ToastController} from '@ionic/angular';
import {ApplicationState} from 'src/app/models/applicationState';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {CaseValue, DependencyValue, LanguageShortcut, PartOfSpeechValue} from 'src/app/models/enum';
import {TranslateService} from '@ngx-translate/core';
import {Storage} from '@ionic/storage';
import {Language} from 'src/app/models/language';
import {ReplaySubject} from 'rxjs';
import {TextData} from './models/textData';
import configMC from '../configMC';
import EventRegistry from './models/eventRegistry';
import {UpdateInfo} from './models/updateInfo';
import {NavigationOptions} from '@ionic/angular/providers/nav-controller';

declare var H5P: any;
// dirty hack to prevent H5P access errors after resize events
window.onresize = () => {
    /* tslint:disable:prefer-const */
    /* tslint:disable:no-shadowed-variable */
    let H5P: any;
    /* tslint:enable:prefer-const */
    /* tslint:enable:no-shadowed-variable */
};

@Injectable({
    providedIn: 'root'
})
export class HelperService {
    public static generalErrorAlertMessage: string;
    public applicationState: ReplaySubject<ApplicationState> = null;
    public applicationStateCache: ApplicationState = null;
    public baseUrl: string = location.protocol.concat('//').concat(window.location.host) +
        window.location.pathname.split('/').slice(0, -1).join('/');
    public caseMap: { [rawValue: string]: CaseValue } = {
        Nom: CaseValue.nominative,
        Gen: CaseValue.genitive,
        Dat: CaseValue.dative,
        Acc: CaseValue.accusative,
        Abl: CaseValue.ablative,
        Voc: CaseValue.vocative,
        Loc: CaseValue.locative,
    };
    public corpusUpdateCompletedString: string;
    public currentError: HttpErrorResponse;
    public currentLanguage: Language;
    public currentPopover: HTMLIonPopoverElement;
    public dependencyMap: { [rawValue: string]: DependencyValue } = {
        acl: DependencyValue.adjectivalClause,
        advcl: DependencyValue.adverbialClauseModifier,
        advmod: DependencyValue.adverbialModifier,
        amod: DependencyValue.adjectivalModifier,
        appos: DependencyValue.appositionalModifier,
        aux: DependencyValue.auxiliary,
        'aux:pass': DependencyValue.auxiliary,
        case: DependencyValue.caseMarking,
        cc: DependencyValue.coordinatingConjunction,
        ccomp: DependencyValue.clausalComplement,
        clf: DependencyValue.classifier,
        compound: DependencyValue.multiwordExpression,
        conj: DependencyValue.conjunct,
        cop: DependencyValue.copula,
        csubj: DependencyValue.subject,
        'csubj:pass': DependencyValue.subject,
        det: DependencyValue.determiner,
        discourse: DependencyValue.discourseElement,
        dislocated: DependencyValue.dislocated,
        expl: DependencyValue.expletive,
        fixed: DependencyValue.multiwordExpression,
        flat: DependencyValue.multiwordExpression,
        goeswith: DependencyValue.goesWith,
        iobj: DependencyValue.object,
        list: DependencyValue.list,
        mark: DependencyValue.marker,
        nmod: DependencyValue.nominalModifier,
        'nmod:poss': DependencyValue.nominalModifier,
        nummod: DependencyValue.numericModifier,
        nsubj: DependencyValue.subject,
        'nsubj:pass': DependencyValue.subject,
        obj: DependencyValue.object,
        obl: DependencyValue.oblique,
        orphan: DependencyValue.orphan,
        parataxis: DependencyValue.parataxis,
        punct: DependencyValue.punctuation,
        root: DependencyValue.root,
        vocative: DependencyValue.vocative,
        xcomp: DependencyValue.clausalComplement,
    };
    public events: EventRegistry = new EventRegistry();
    public isIE11: boolean = !!(window as any).MSInputMethodContext && !!(document as any).documentMode;
    public isDevMode = ['localhost'].indexOf(window.location.hostname) > -1; // set this to "false" for simulated production mode
    public isVocabularyCheck = false;
    public languages: Language[] = [
        new Language({name: 'English', shortcut: LanguageShortcut.English}),
        new Language({name: 'Deutsch', shortcut: LanguageShortcut.German})];
    public openRequests: string[] = [];
    public partOfSpeechMap: { [rawValue: string]: PartOfSpeechValue } = {
        ADJ: PartOfSpeechValue.adjective,
        ADP: PartOfSpeechValue.preposition,
        ADV: PartOfSpeechValue.adverb,
        AUX: PartOfSpeechValue.auxiliary,
        CCONJ: PartOfSpeechValue.conjunction,
        DET: PartOfSpeechValue.pronoun,
        INTJ: PartOfSpeechValue.interjection,
        NOUN: PartOfSpeechValue.noun,
        NUM: PartOfSpeechValue.numeral,
        PART: PartOfSpeechValue.particle,
        PRON: PartOfSpeechValue.pronoun,
        PROPN: PartOfSpeechValue.properNoun,
        PUNCT: PartOfSpeechValue.punctuation,
        SCONJ: PartOfSpeechValue.conjunction,
        SYM: PartOfSpeechValue.symbol,
        VERB: PartOfSpeechValue.verb,
        X: PartOfSpeechValue.other
    };
    public shareLinkSelector = 'input[type="text"]';

    constructor(public http: HttpClient,
                public storage: Storage,
                public translate: TranslateService,
    ) {
        this.initLanguage();
        this.initConfig();
        this.initApplicationState();
    }

    // The translate loader needs to know where to load i18n files in Ionic's static asset pipeline.
    static createTranslateLoader(http: HttpClient): TranslateHttpLoader {
        return new TranslateHttpLoader(http, './assets/i18n/', '.json');
    }

    /**
     * Shuffles array in place.
     * @param array items An array containing the items.
     */
    static shuffle(array: Array<any>): Array<any> {
        let j, x, i;
        for (i = array.length - 1; i > 0; i--) {
            j = Math.floor(Math.random() * (i + 1));
            x = array[i];
            array[i] = array[j];
            array[j] = x;
        }
        return array;
    }

    copyShareLink(toastCtrl: ToastController, shareLinkCopiedString: string): void {
        const input: HTMLInputElement = document.querySelector(this.shareLinkSelector);
        input.select();
        document.execCommand('copy');
        input.setSelectionRange(0, 0);
        this.showToast(toastCtrl, shareLinkCopiedString, 'middle').then();
    }

    deepCopy(obj: object): any {
        let copy;
        // Handle the 3 simple types, and null or undefined
        if (null === obj || 'object' !== typeof obj) {
            return obj;
        }
        // Handle Date
        if (obj instanceof Date) {
            copy = new Date();
            copy.setTime(obj.getTime());
            return copy;
        }
        // Handle Array
        if (obj instanceof Array) {
            copy = [];
            for (let i = 0, len = obj.length; i < len; i++) {
                copy[i] = this.deepCopy(obj[i]);
            }
            return copy;
        }
        // Handle Object
        if (obj instanceof Object) {
            copy = {};
            for (const attr in obj) {
                if (obj.hasOwnProperty(attr)) {
                    copy[attr] = this.deepCopy(obj[attr]);
                }
            }
            return copy;
        }
    }

    getDelayedTranslation(translate: TranslateService, key: string) {
        return new Promise(resolve => {
            translate.get(key).subscribe((value: string) => {
                // check if we got the correct translated value
                if (value === value.toUpperCase()) {
                    setTimeout(() => {
                        translate.get(key).subscribe(value2 => resolve(value2));
                    }, 1000);
                } else {
                    resolve(value);
                }
            });
        });
    }

    getEnumValues(target: any): string[] {
        return Object.keys(target).filter((value, index, array) => {
            return index % 2 !== 0;
        });
    }

    getFormForPostRequest(form: object): HttpParams {
        let params: HttpParams = new HttpParams();
        Object.keys(form).forEach((key: string) => {
            params = params.set(key, form[key]);
        });
        return params;
    }

    getH5P(): any {
        return H5P;
    }

    goToAuthorPage(navCtrl: NavController): Promise<boolean> {
        this.isVocabularyCheck = false;
        return this.goToPage(navCtrl, configMC.pageUrlAuthor);
    }

    goToHomePage(navCtrl: NavController): Promise<boolean> {
        return navCtrl.navigateRoot(configMC.pageUrlHome);
    }

    goToPage(navCtrl: NavController, pageUrl: string, options?: NavigationOptions): Promise<boolean> {
        return navCtrl.navigateForward(pageUrl, options);
    }

    goToShowTextPage(navCtrl: NavController, isVocabularyCheck: boolean = false): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.goToPage(navCtrl, configMC.pageUrlShowText).then((result: boolean) => {
                this.isVocabularyCheck = isVocabularyCheck;
                return resolve(result);
            });
        });
    }

    goToTestPage(navCtrl: NavController): Promise<boolean> {
        return navCtrl.navigateRoot(configMC.pageUrlTest);
    }

    handleRequestError(toastCtrl: ToastController, error: HttpErrorResponse, errorMessage: string, url: string): void {
        this.openRequests.splice(this.openRequests.indexOf(url), 1);
        this.currentError = error;
        this.showToast(toastCtrl, errorMessage).then();
    }

    initApplicationState(): void {
        this.applicationState = new ReplaySubject<ApplicationState>(1);
        if (!this.applicationStateCache) {
            this.storage.get(configMC.localStorageKeyApplicationState).then((jsonString: string) => {
                this.applicationStateCache = new ApplicationState({
                    exerciseList: [],
                    mostRecentSetup: new TextData()
                });
                if (jsonString) {
                    const jsonObject: any = this.updateAnnisResponse(jsonString);
                    const state: ApplicationState = jsonObject as ApplicationState;
                    state.exerciseList = state.exerciseList ? state.exerciseList : [];
                    state.mostRecentSetup = state.mostRecentSetup ? state.mostRecentSetup : new TextData();
                    this.applicationStateCache = state;
                }
                this.applicationState.next(this.applicationStateCache);
            });
        } else {
            this.applicationState.next(this.applicationStateCache);
        }
    }

    initConfig(): void {
        const part1: string = location.protocol.concat('//').concat(window.location.host);
        configMC.backendBaseUrl = part1.concat(configMC.backendBaseApiPath).concat('/');
        configMC.frontendBaseUrl = location.href.substring(0, location.href.lastIndexOf('/'));
    }

    initLanguage(): void {
        // dirty hack to wait for the translateService intializing
        setTimeout(() => {
            this.currentLanguage = this.languages.find(
                x => x.shortcut.toString() === this.translate.currentLang);
            this.loadTranslations(this.translate);
        });
    }

    initUpdateInfo(): Promise<void> {
        return new Promise<void>(resolve => {
            this.storage.get(configMC.localStorageKeyUpdateInfo).then((jsonString: string) => {
                if (jsonString) {
                    return resolve();
                }
                const ui: UpdateInfo = new UpdateInfo({
                    corpora: 1,
                    exerciseList: 1
                });
                this.storage.set(configMC.localStorageKeyUpdateInfo, JSON.stringify(ui)).then(() => {
                    return resolve();
                });
            });
        });
    }

    loadTranslations(translate: TranslateService): void {
        // dirty hack to wait until the translation loader is initialized in IE11
        this.getDelayedTranslation(translate, 'CORPUS_UPDATE_COMPLETED').then((value: string) => {
            this.corpusUpdateCompletedString = value;
        });
        this.getDelayedTranslation(translate, 'ERROR_GENERAL_ALERT').then((value: string) => {
            HelperService.generalErrorAlertMessage = value;
        });
    }

    makeGetRequest(http: HttpClient, toastCtrl: ToastController, url: string,
                   params: HttpParams = new HttpParams(),
                   errorMessage: string = HelperService.generalErrorAlertMessage,
                   responseType: any = 'json'): Promise<any> {
        return new Promise(((resolve, reject) => {
            this.currentError = null;
            this.openRequests.push(url);
            http.get(url, {params, responseType}).subscribe((result: any) => {
                this.openRequests.splice(this.openRequests.indexOf(url), 1);
                return resolve(result);
            }, async (error: HttpErrorResponse) => {
                this.handleRequestError(toastCtrl, error, errorMessage, url);
                return reject(error);
            });
        }));
    }

    makePostRequest(http: HttpClient, toastCtrl: ToastController, url: string, body: HttpParams,
                    errorMessage: string = HelperService.generalErrorAlertMessage, options: any = {}): Promise<any> {
        return new Promise(((resolve, reject) => {
            this.currentError = null;
            this.openRequests.push(url);
            options.headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
            http.post(url, body, options).subscribe((result: any) => {
                this.openRequests.splice(this.openRequests.indexOf(url), 1);
                return resolve(result);
            }, async (error: HttpErrorResponse) => {
                this.handleRequestError(toastCtrl, error, errorMessage, url);
                return reject(error);
            });
        }));
    }

    saveApplicationState(state: ApplicationState): Promise<void> {
        return new Promise((resolve) => {
            this.applicationStateCache = state;
            this.applicationState.next(this.applicationStateCache);
            this.storage.set(configMC.localStorageKeyApplicationState, JSON.stringify(state)).then(() => {
                return resolve();
            });
        });
    }

    selectShareLink(): void {
        const ta: HTMLInputElement = document.querySelector(this.shareLinkSelector);
        ta.select();
    }

    showToast(toastCtrl: ToastController, message: string, position: any = 'top'): Promise<void> {
        return toastCtrl.create({
            message,
            duration: 3000,
            position
        }).then((toast: HTMLIonToastElement) => toast.present());
    }

    updateAnnisResponse(jsonString: string): any {
        const jsonObject: any = JSON.parse(jsonString);
        const mrs: any = jsonObject.mostRecentSetup;
        // backwards compatibility
        if (mrs && mrs.annisResponse && !mrs.annisResponse.graph_data) {
            const annisResp: any = mrs.annisResponse;
            const props: string[] = ['links', 'nodes', 'directed', 'graph', 'multigraph'];
            annisResp.graph_data = {};
            props.forEach((prop: string) => {
                annisResp.graph_data[prop] = annisResp[prop];
                delete annisResp[prop];
            });
        }
        return jsonObject;
    }
}
